import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Phone, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { otpApi } from "@/lib/api";

export default function OtpDialog({ open, onOpenChange, onVerified }) {
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");

  const reset = () => { setStep("phone"); setPhone(""); setOtp(""); setHint(""); };

  const handleSend = async () => {
    const cleaned = phone.trim();
    if (!/^\+?[0-9\s\-]{7,15}$/.test(cleaned)) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    setLoading(true);
    try {
      const res = await otpApi.send(cleaned);
      setStep("otp");
      setHint(`Demo OTP for testing: ${res.demo_otp}`);
      toast.success(`OTP sent to ${cleaned}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { toast.error("Enter 6-digit OTP"); return; }
    setLoading(true);
    try {
      await otpApi.verify(phone.trim(), otp);
      toast.success("Mobile verified successfully");
      onVerified?.(phone.trim());
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent
        data-testid="otp-dialog"
        className="max-w-md rounded-3xl bg-white p-8 border border-black/10"
      >
        <DialogHeader>
          <div className="h-12 w-12 rounded-2xl bg-[#FA003F]/10 text-[#FA003F] grid place-items-center mb-2">
            {step === "phone" ? <Phone className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
          </div>
          <DialogTitle className="font-display text-2xl">
            {step === "phone" ? "Verify your mobile" : "Enter OTP"}
          </DialogTitle>
          <DialogDescription>
            {step === "phone"
              ? "We'll send a 6-digit code to unlock booking details."
              : `Enter the 6-digit code sent to ${phone}`}
          </DialogDescription>
        </DialogHeader>

        {step === "phone" ? (
          <div className="space-y-4">
            <Input
              data-testid="otp-phone-input"
              placeholder="+91 98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-12 rounded-xl text-base"
              autoFocus
            />
            <Button
              data-testid="otp-send-btn"
              disabled={loading}
              onClick={handleSend}
              className="w-full h-12 rounded-full bg-[#FA003F] hover:bg-[#C40031] text-base font-bold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send OTP"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                <InputOTPGroup className="gap-2">
                  {[0,1,2,3,4,5].map((i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className="h-12 w-11 text-lg font-bold rounded-lg border border-black/15 first:rounded-l-lg last:rounded-r-lg"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {hint && (
              <div className="text-xs text-center text-gray-500 bg-[#FA003F]/5 rounded-lg py-2 px-3">
                {hint}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setStep("phone"); setOtp(""); }}
                className="h-12 rounded-full flex-1"
                data-testid="otp-back-btn"
              >
                Change number
              </Button>
              <Button
                data-testid="otp-verify-btn"
                disabled={loading || otp.length !== 6}
                onClick={handleVerify}
                className="h-12 rounded-full flex-1 bg-[#1A1A1A] hover:bg-[#FA003F] text-base font-bold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
